import React, { useState } from 'react';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import { communityTiers } from '../mock/data';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import './Community.css';

const Community = () => {
  const { toast } = useToast();
  const [selectedTier, setSelectedTier] = useState(null);

  const handleJoinTier = async (tier) => {
    if (tier.price === 0) {
      // Free tier - just show success message
      toast({
        title: "Welcome to the Community!",
        description: "Check your email for next steps.",
      });
      return;
    }

    // For paid tiers, we'll integrate Stripe payment
    toast({
      title: "Payment Processing",
      description: "Redirecting to secure checkout...",
    });

    // MOCK: In real implementation, this will call backend to create Stripe checkout session
    console.log('Initiating payment for:', tier.name);
  };

  return (
    <div className="community-page">
      {/* Hero */}
      <section className="community-hero-section">
        <div className="section-content">
          <div className="community-hero-badge">
            <Sparkles size={16} />
            <span>Exclusive Entrepreneur Network</span>
          </div>
          <h1 className="display-huge">The Business Class Community</h1>
          <p className="body-large community-hero-subtitle">
            Join serious founders building high-performance businesses. No fluff, only value. 
            Growth-focused environment for entrepreneurs scaling with excellence.
          </p>
        </div>
      </section>

      {/* What Members Get */}
      <section className="community-benefits-section">
        <div className="section-content">
          <h2 className="display-medium text-center">What You Get</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <h3 className="heading-2">Peer Masterminds</h3>
              <p className="body-medium">
                Weekly sessions with vetted founders who understand your challenges and push you forward.
              </p>
            </div>
            <div className="benefit-card">
              <h3 className="heading-2">Growth Frameworks</h3>
              <p className="body-medium">
                Battle-tested systems, templates, and playbooks you can implement immediately.
              </p>
            </div>
            <div className="benefit-card">
              <h3 className="heading-2">Expert Coaching</h3>
              <p className="body-medium">
                Regular strategy calls with experienced business operators and consultants.
              </p>
            </div>
            <div className="benefit-card">
              <h3 className="heading-2">Networking</h3>
              <p className="body-medium">
                Connect with founders, potential partners, and collaborators building real businesses.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Tiers */}
      <section className="community-tiers-section">
        <div className="section-content">
          <h2 className="display-medium text-center">Choose Your Tier</h2>
          <p className="body-medium text-center section-subtitle">
            Select the level that matches your growth stage
          </p>
          <div className="tiers-grid">
            {communityTiers.map(tier => (
              <Card 
                key={tier.id} 
                className={`tier-card ${tier.popular ? 'popular-tier' : ''}`}
              >
                {tier.popular && (
                  <div className="popular-badge">Most Popular</div>
                )}
                <div className="tier-card-content">
                  <div className="tier-header">
                    <h3 className="heading-1">{tier.name}</h3>
                    <div className="tier-price">
                      {tier.price === 0 ? (
                        <span className="price-free">Free</span>
                      ) : (
                        <>
                          <span className="price-currency">₹</span>
                          <span className="price-amount">{tier.price.toLocaleString('en-IN')}</span>
                          <span className="price-billing">/{tier.billing}</span>
                        </>
                      )}
                    </div>
                    <p className="body-medium tier-description">{tier.description}</p>
                  </div>
                  
                  <div className="tier-features">
                    {tier.features.map((feature, idx) => (
                      <div key={idx} className="tier-feature-item">
                        <Check size={20} className="feature-check" />
                        <span className="body-small">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    className={tier.popular ? "btn-primary w-full" : "btn-secondary w-full"}
                    onClick={() => handleJoinTier(tier)}
                  >
                    <span>{tier.cta}</span>
                    <ArrowRight size={20} />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="community-process-section">
        <div className="section-content">
          <h2 className="display-medium text-center">How to Join</h2>
          <div className="join-process-steps">
            <div className="join-step">
              <div className="join-step-number">1</div>
              <h3 className="heading-2">Choose Your Tier</h3>
              <p className="body-medium">Select the membership level that fits your goals</p>
            </div>
            <div className="join-step">
              <div className="join-step-number">2</div>
              <h3 className="heading-2">Complete Application</h3>
              <p className="body-medium">Tell us about your business and what you're building</p>
            </div>
            <div className="join-step">
              <div className="join-step-number">3</div>
              <h3 className="heading-2">Get Approved</h3>
              <p className="body-medium">We review to ensure community quality and fit</p>
            </div>
            <div className="join-step">
              <div className="join-step-number">4</div>
              <h3 className="heading-2">Start Growing</h3>
              <p className="body-medium">Immediate access to resources, calls, and network</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="community-for-section">
        <div className="section-content">
          <div className="community-for-grid">
            <div className="for-section">
              <h2 className="display-medium">This Community Is For</h2>
              <ul className="for-list">
                <li className="body-medium">Founders scaling from ₹10L to ₹10Cr+</li>
                <li className="body-medium">Service business owners seeking systemization</li>
                <li className="body-medium">Entrepreneurs committed to excellence</li>
                <li className="body-medium">Leaders building high-performance teams</li>
                <li className="body-medium">Business owners ready to invest in growth</li>
              </ul>
            </div>
            <div className="not-for-section">
              <h2 className="display-medium">This Community Is NOT For</h2>
              <ul className="not-for-list">
                <li className="body-medium">People looking for get-rich-quick schemes</li>
                <li className="body-medium">Those unwilling to implement and execute</li>
                <li className="body-medium">Casual networkers without clear goals</li>
                <li className="body-medium">Businesses not generating revenue</li>
                <li className="body-medium">Anyone seeking passive learning only</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="community-final-cta-section">
        <div className="section-content">
          <div className="community-final-cta-content">
            <h2 className="display-large">Ready to Elevate Your Network?</h2>
            <p className="body-large">
              Join founders who are serious about building business class companies.
            </p>
            <Button className="btn-primary large-btn" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <span>View Membership Tiers</span>
              <ArrowRight size={24} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Community;
